import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom icons for different location types
const createCustomIcon = (color) => {
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="
      background-color: ${color};
      width: 24px;
      height: 24px;
      border-radius: 50% 50% 50% 0;
      transform: rotate(-45deg);
      border: 2px solid white;
      box-shadow: 0 2px 4px rgba(0,0,0,0.3);
    "></div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 24],
    popupAnchor: [0, -24],
  });
};

export const beachIcon = createCustomIcon('#0d9488'); // teal-600
export const parkingIcon = createCustomIcon('#3b82f6'); // blue-500
export const freeIcon = createCustomIcon('#10b981'); // emerald-500

export const LocationMap = ({ 
  locations, 
  center = [40.95, 17.29], // Default to Monopoli area
  zoom = 11,
  height = "300px",
  type = "beach" // "beach" | "parking"
}) => {
  if (!locations || locations.length === 0) return null;

  // Calculate center from locations if not provided
  const validLocations = locations.filter(loc => loc.coordinates?.lat && loc.coordinates?.lng);
  
  if (validLocations.length === 0) return null;

  const avgLat = validLocations.reduce((sum, loc) => sum + loc.coordinates.lat, 0) / validLocations.length;
  const avgLng = validLocations.reduce((sum, loc) => sum + loc.coordinates.lng, 0) / validLocations.length;

  const getIcon = (location) => {
    if (type === "parking") {
      return location.spot_type === "free" ? freeIcon : parkingIcon;
    }
    return beachIcon;
  };

  return (
    <div className="rounded-xl overflow-hidden border border-stone-200 shadow-sm" style={{ height }}>
      <MapContainer
        center={[avgLat, avgLng]}
        zoom={zoom}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {validLocations.map((location) => (
          <Marker
            key={location.id}
            position={[location.coordinates.lat, location.coordinates.lng]}
            icon={getIcon(location)}
          >
            <Popup>
              <div className="text-sm">
                <p className="font-semibold text-stone-900">{location.name}</p>
                <p className="text-stone-600 text-xs mt-1">
                  {type === "parking" ? location.area : location.town}
                </p>
                {type === "parking" && location.cost && (
                  <p className="text-teal-700 font-medium mt-1">{location.cost}</p>
                )}
                {type === "beach" && location.beach_type && (
                  <p className="text-teal-700 capitalize mt-1">{location.beach_type}</p>
                )}
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
};

export const SingleLocationMap = ({ 
  lat, 
  lng, 
  name,
  type = "beach",
  height = "200px" 
}) => {
  if (!lat || !lng) return null;

  const icon = type === "parking" ? parkingIcon : beachIcon;

  return (
    <div className="rounded-xl overflow-hidden border border-stone-200" style={{ height }}>
      <MapContainer
        center={[lat, lng]}
        zoom={15}
        style={{ height: '100%', width: '100%' }}
        scrollWheelZoom={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <Marker position={[lat, lng]} icon={icon}>
          <Popup>{name}</Popup>
        </Marker>
      </MapContainer>
    </div>
  );
};
